import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps, useLocation, withRouter } from 'react-router-dom';

import { makeStyles } from '@material-ui/styles';

import TaskStatus from '@vestahealthcare/common/enums/TaskStatus';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Employee,
  EmployeeGroup,
  Encounter,
  Task,
} from '@vestahealthcare/common/models';
import TaskCustomField from '@vestahealthcare/common/models/TaskCustomField';
import TaskDefinition from '@vestahealthcare/common/models/TaskDefinition';
import TaskDefinitionOutcome from '@vestahealthcare/common/models/TaskDefinitionOutcome';
import TaskNote from '@vestahealthcare/common/models/TaskNote';
import { DATE_FORMAT_SHORT_WITH_TIME } from '@vestahealthcare/common/utils/constants';

import { Modal, Section } from 'styleguide';
import {
  Button,
  DateTimePicker,
  Fonts,
  Select,
  SelectAssignee,
  TextArea,
  UNASSIGNED,
} from 'styleguide-v2';

import { AddEncounterButton } from 'dash/src/pages/MemberProfile/Encounters/AddEncounterButton';
import { CacheServices } from 'dash/src/services';
import { fetchById as fetchMemberById } from 'dash/src/services/PatientServices';
import { orderRPMDevices } from 'dash/src/services/RPMVendorServices';
import Session from 'dash/src/services/SessionServices';
import {
  editTask,
  fetchTask,
  fetchTaskDefinitionOutcomes,
  fetchTaskNotes,
  updateTaskCustomField,
} from 'dash/src/services/TaskServices';

import { fetchAllEncounters } from '../../services/EncounterServices';
import { useFlags } from '../../utils/flagUtils';
import { ContactInfoModal } from '../MemberProfile/Header/ContactInfoModal';
import EditTaskCustomField from './EditTaskCustomField';
import { OrderDeviceModal } from './OrderDeviceModal';
import TaskNoteDetail from './TaskNote';
import { getDueDate, roundToNearest30 } from './utils';

const COLUMNS = 2;

const useStyles = makeStyles({
  button: {
    minWidth: '15rem',
  },
  section: {
    marginBottom: 10,
  },
  details: {
    padding: 20,
  },
  header: {
    margin: -20,
    paddingBottom: 5,
    marginBottom: 10,
  },
});

interface CustomFieldAnswer {
  [key: string]: string | undefined;
}

const getCustomFieldAnswers = (customFields?: TaskCustomField[]) => {
  if (!customFields) return {};

  const answers: CustomFieldAnswer = {};
  customFields.forEach((c) => (answers[`${c.id}`] = c.value));
  return answers;
};

interface Props {
  task: Task;
  hideAssignee?: boolean;
  hideGroup?: boolean;
  hideStatus?: boolean;
  onUpdate: (newTask: Task) => void;
}

export const TaskDetails = ({
  task,
  hideAssignee,
  hideGroup,
  hideStatus,
  onUpdate,
  history,
}: Props & RouteComponentProps) => {
  const { pathname } = useLocation();
  const { disableRpmOrders } = useFlags();
  const isTaskDashboard = pathname.includes('dashboard/tasks');

  const [member, setMember] = useState(task.member);
  const [notes, setNotes] = useState<TaskNote[]>([]);
  const [description, setDescription] = useState<string>();
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [outcome, setOutcome] = useState<TaskDefinitionOutcome | undefined>(
    task.taskDefinitionOutcome,
  );
  const [employeeGroupId, setEmployeeGroupId] = useState<EmployeeGroup>(
    task.employeeGroup,
  );
  const [assigneeId, setAssigneeId] = useState<Employee | undefined>(
    task.assignee,
  );
  const [date, setDate] = useState<Moment | undefined>(
    task.dueDate ? moment.unix(task.dueDate) : undefined,
  );
  const [time, setTime] = useState<Moment | undefined>(
    task.dueDate ? moment.unix(task.dueDate) : undefined,
  );
  const [customFieldAnswers, setCustomFieldAnswers] = useState<
    CustomFieldAnswer
  >(getCustomFieldAnswers(task.customFields));

  const [showOpenSubtaskWarning, setShowOpenSubtasksWarning] = useState(false);
  const [showNoOutcomeError, setShowNoOutcomeError] = useState(false);
  const [showNoCustomFieldError, setShowNoCustomFieldError] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showOrderDeviceModal, setShowOrderDeviceModal] = useState(false);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeGroups, setEmployeeGroups] = useState<EmployeeGroup[]>([]);
  const [definitionOutcomes, setDefinitionOutcomes] = useState<
    TaskDefinitionOutcome[]
  >([]);
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isClosed = task.status.closed && !Session.actingUser.canEditTaskClosed;

  const styles = useStyles();

  const filterOutcomes = (task: Task) => {
    if (task.status === TaskStatus.NOT_STARTED) {
      return ({ enabled }: TaskDefinitionOutcome) => enabled;
    }
    if (TaskStatus.getNotClosedStatuses().includes(task.status.value)) {
      return ({ enabled, id }: TaskDefinitionOutcome) =>
        enabled || task.taskDefinitionOutcome?.id === id;
    }
    return () => true;
  };

  useEffect(() => {
    (async () => {
      const taskIds = [task.id];

      if (task.children) {
        taskIds.push(...task.children.map((c) => c.id));
      }

      const [o, e, g, n, en] = await Promise.all([
        fetchTaskDefinitionOutcomes(task.taskDefinition.id),
        CacheServices.getEmployees(),
        CacheServices.getEmployeeGroupsAssignee(),
        fetchTaskNotes(task.id),
        task.member
          ? fetchAllEncounters(task.member.id, { taskIds })
          : { items: [] as Encounter[] },
      ]);
      setDefinitionOutcomes(o.filter(filterOutcomes(task)));
      setEmployees(e);
      setEmployeeGroups(g);
      setNotes(n);
      setEncounters(en.items);

      if (task.member) {
        const m = await fetchMemberById(task.member.id);
        setMember(m);
      }
    })();
  }, [task]);

  const onChangeEmployeeGroup = (group?: EmployeeGroup) => {
    if (group) {
      setEmployeeGroupId(group);

      if (group.isNPOwner() && member?.npOwner) {
        setAssigneeId(member.npOwner);
      } else if (group.isRNOwner() && member?.owner) {
        setAssigneeId(member.owner);
      } else {
        setAssigneeId(undefined);
      }
    }
  };

  const updateCustomFields = async () => {
    const updateCustomFieldPromises: Array<Promise<any>> = [];
    const customFieldAnswerKeys = Object.keys(customFieldAnswers);
    if (customFieldAnswerKeys.length > 0) {
      customFieldAnswerKeys.forEach((key) => {
        const answer = customFieldAnswers[key];
        const customField = task.customFields?.find(
          (c) => c.id === Number(key),
        );

        // if the value was changed
        if (customField?.value !== answer) {
          updateCustomFieldPromises.push(
            updateTaskCustomField(task.id, key, answer),
          );
        }
      });
    }

    await Promise.all(updateCustomFieldPromises);
  };

  const hasMissingCustomFields = () => {
    const customFieldWithoutAnswer = task.customFields?.find((cf) => {
      if (cf.required && !customFieldAnswers[cf.id]) {
        return cf;
      }

      return undefined;
    });

    return !!customFieldWithoutAnswer;
  };

  const updateTask = async () => {
    const newTaskDefinitionOutcomeId =
      task.taskDefinitionOutcome === outcome ? undefined : outcome?.id;
    const newStatus = status?.value === task.status.value ? undefined : status;
    const newEmployeeGroupId =
      employeeGroupId?.id === task.employeeGroup?.id
        ? undefined
        : employeeGroupId?.id;
    const newAssigneeId =
      assigneeId === task.assignee?.id ? undefined : assigneeId?.id;

    // Update the custom fields first because there's some backend logic where some custom fields
    // are required to mark a task as COMPLETED
    await updateCustomFields();

    const newTask = await editTask(task.id, {
      status: newStatus?.value,
      assigneeId: newAssigneeId,
      employeeGroupId: newEmployeeGroupId,
      taskDefinitionOutcomeId: newTaskDefinitionOutcomeId,
      dueDate: getDueDate(date, time),
      note: description,
    });

    setDescription(undefined);
    onUpdate && (await onUpdate(newTask));

    const notes = await fetchTaskNotes(task.id);
    setNotes(notes);
  };

  const onUpdateTask = async () => {
    setSubmitted(true);
    if (status) {
      if (!outcome && task.isOutcomeRequiredForStatus(status)) {
        setShowNoOutcomeError(true);
      } else if (status === TaskStatus.COMPLETED && hasMissingCustomFields()) {
        setShowNoCustomFieldError(true);
      } else if (status.closed && task.hasOpenSubtasks()) {
        setShowOpenSubtasksWarning(true);
      } else {
        try {
          setLoading(true);
          await updateTask();
        } finally {
          setSubmitted(false);
          setLoading(false);
        }
      }
    }
  };

  return (
    <>
      <Section className={styles.section}>
        <Section.Body>
          <div className={`grid-wrapper ${styles.details}`}>
            <header className={`grid-span-12 h4 ${styles.header}`}>
              {translate('tasks.taskDetails')}
            </header>
            <div className="grid-span-10">
              {task.description || task.taskDefinition.name}
              &nbsp;
              {task.member && task.eventId && (
                <a href={`#/patients/${task.member.id}/events/${task.eventId}`}>
                  ({translate('tasks.createdFromEvent')})
                </a>
              )}
            </div>
            <div className="grid-span-10" />
            <div className="grid-span-2">
              <div className="pull-right">
                {encounters.length > 0 && (
                  <>
                    <a
                      href={`/#/patients/${
                        task.member?.id
                      }/encounters?ids=${encounters
                        .map((en) => en.id)
                        .join(',')}`}
                    >
                      {translate(
                        encounters.length > 1
                          ? 'tasks.encounters'
                          : 'tasks.encounter',
                        { count: encounters.length },
                      )}
                    </a>{' '}
                    (
                    {translate('encounters.minutes', {
                      minutes: encounters.reduce(
                        (prev, curr) => prev + curr.duration,
                        0,
                      ),
                    })}
                    )
                    {encounters.map((encounter) => (
                      <p key={encounter.id} className="small no-margin">
                        {translate('personalDetails.encounterAddedFormatted', {
                          formattedDate: moment
                            .unix(encounter.createdAt)
                            .format(DATE_FORMAT_SHORT_WITH_TIME),
                        })}
                      </p>
                    ))}
                  </>
                )}
              </div>
            </div>
            {!hideStatus && (
              <Select
                className="grid-span-2"
                disabled={isClosed}
                error={
                  submitted &&
                  !status &&
                  translate('global.missingRequiredField')
                }
                getItemLabel={(item: TaskStatus) => item.toString()}
                items={TaskStatus.asArray}
                label={translate('tasks.status')}
                onChange={setStatus}
                required
                value={status}
              />
            )}
            <Select
              className="grid-span-2"
              disabled={isClosed}
              error={showNoOutcomeError && translate('tasks.outcomeIsRequired')}
              getItemDisabled={(item: TaskDefinitionOutcome) => !item.enabled}
              getItemLabel={(item: TaskDefinitionOutcome) => item.name}
              items={definitionOutcomes}
              label={translate('tasks.taskOutcome')}
              onChange={(val: TaskDefinitionOutcome) => {
                setOutcome(val);
                setShowNoOutcomeError(false);
              }}
              required={task.isOutcomeRequiredForStatus(status)}
              value={outcome}
            />
            {!hideGroup && (
              <Select
                className="grid-span-2"
                disabled={isClosed}
                getItemLabel={(item: EmployeeGroup) => item.name}
                items={employeeGroups}
                label={translate('tasks.group')}
                onChange={onChangeEmployeeGroup}
                value={employeeGroupId}
              />
            )}
            {!hideAssignee && (
              <SelectAssignee
                allowUnassigned
                assignee={assigneeId || UNASSIGNED}
                className="grid-span-2"
                currentUser={Session.actingUser}
                label={translate('tasks.assignedTo')}
                items={employees}
                onChange={(assignee?: Employee | EmployeeGroup) =>
                  setAssigneeId(assignee as Employee)
                }
                disabled={isClosed}
              />
            )}

            <DateTimePicker
              className="grid-span-2"
              disabled={isClosed}
              label={translate('tasks.dueDate')}
              onChange={(date?: Date) =>
                setDate(date ? moment(date.getTime()) : undefined)
              }
              minDate={Date.now()}
              value={date?.toDate()}
            />
            <DateTimePicker
              className="grid-span-2"
              disabled={isClosed}
              type="time"
              label={translate('tasks.dueTime')}
              onChange={(val: Date) => {
                if (!date) {
                  setDate(val ? moment(val.getTime()) : undefined);
                }
                setTime(val ? moment(val.getTime()) : undefined);
              }}
              minDate={
                date?.isSame(moment(), 'day')
                  ? roundToNearest30(moment()).toDate()
                  : moment().startOf('day').toDate()
              }
              value={time?.toDate()}
            />

            {task.customFields?.map((customField) => (
              <EditTaskCustomField
                columns={COLUMNS}
                customField={customField}
                disabled={isClosed}
                required={
                  customField.required && status === TaskStatus.COMPLETED
                }
                key={customField.id}
                onUpdate={(answer) =>
                  setCustomFieldAnswers({
                    ...customFieldAnswers,
                    [customField.id]: answer,
                  })
                }
                showError={
                  customField.required &&
                  showNoCustomFieldError &&
                  !customFieldAnswers[customField.id]
                }
                value={customFieldAnswers[customField.id]}
              />
            ))}

            <TextArea
              className="grid-span-12"
              disabled={isClosed}
              label={translate('global.note')}
              onChange={setDescription}
              rows={2}
              value={description}
            />
          </div>
          <div
            className="flex extra-gap"
            style={{
              justifyContent: 'flex-end',
              padding: '1rem 2rem',
              fontSize: `calc(${Fonts.fontSize} * 0.875)`,
            }}
          >
            {member && (
              <AddEncounterButton
                className={styles.button}
                task={task}
                patientId={member.id}
                onSubmit={() => onUpdate(task)}
              />
            )}
            <Button
              className={styles.button}
              color="secondary"
              type="outlined"
              onClick={() => history.push(`/dashboard/tasks/${task.id}`)}
            >
              {translate('tasks.goToTaskPage')}
            </Button>
            {task.member && (
              <Button
                className={styles.button}
                color="secondary"
                type="outlined"
                onClick={() => setShowContactModal(true)}
              >
                {translate('personalDetails.contact')}
              </Button>
            )}
            {!disableRpmOrders &&
              task.taskDefinition.id ===
                TaskDefinition.RPM_NEW_ORDER_REQUEST && (
                <Button
                  className={styles.button}
                  color="secondary"
                  disabled={isClosed}
                  loading={loading}
                  onClick={() => setShowOrderDeviceModal(true)}
                >
                  {translate('tasks.orderDevice')}
                </Button>
              )}
            <Button
              className={styles.button}
              color="secondary"
              disabled={isClosed}
              loading={loading}
              onClick={onUpdateTask}
            >
              {translate('tasks.updateTask')}
            </Button>
          </div>
        </Section.Body>

        {notes.map((note) => (
          <TaskNoteDetail key={note.id} taskNote={note} />
        ))}
      </Section>
      {task.taskDefinition.id === TaskDefinition.RPM_NEW_ORDER_REQUEST &&
        member && (
          <OrderDeviceModal
            member={member}
            onClose={() => setShowOrderDeviceModal(false)}
            onSubmit={async (params) => {
              await orderRPMDevices({
                memberId: member.id,
                taskId: task.id,
                ...params,
              });
              const detail = await fetchTask(task.id);
              const notes = await fetchTaskNotes(task.id);

              onUpdate(detail);
              setStatus(detail?.status);
              setNotes(notes);
            }}
            open={showOrderDeviceModal}
          />
        )}
      {showContactModal && member && (
        <ContactInfoModal
          onClose={() => setShowContactModal(false)}
          member={member}
          open={showContactModal}
          getCareTeam={isTaskDashboard}
        />
      )}
      <Modal
        show={showOpenSubtaskWarning}
        color="warning"
        onHide={() => setShowOpenSubtasksWarning(false)}
      >
        <Modal.Header
          closeButton
          modalTitle={
            <>
              <i className="fa fa-warning" /> {translate('global.warning')}
            </>
          }
        />
        <Modal.Body>
          <p>{translate('tasks.attemptingToCloseWithOpenSubtasks')}</p>
        </Modal.Body>
        <Modal.Footer
          leftSide={
            <Button
              color="tertiary"
              onClick={() => setShowOpenSubtasksWarning(false)}
            >
              {translate('global.cancel')}
            </Button>
          }
          rightSide={
            <Button
              color="primary"
              loading={loading}
              onClick={async () => {
                try {
                  setLoading(true);
                  await updateTask();
                  setShowOpenSubtasksWarning(false);
                } finally {
                  setLoading(false);
                }
              }}
            >
              {translate('global.continue')}
            </Button>
          }
        />
      </Modal>
    </>
  );
};

export default withRouter(TaskDetails);
